import { Chip } from "@mui/material"
import { VisibilityOutlined } from "@mui-symbols-material/w300"
import type { ColumnDef } from "@tanstack/react-table"

import { useProductTestResultColumns } from "./useProductTestResultColumns"

import { Button } from "@/components/common/Button"
import type { AssociatedTestTemplate, QualityApproval } from "@/graphql/codegen/graphql"
import { getTestTemplateComparisonLabel } from "@/screens/ManageProducts/components/TestTemplates/utils"
import { formatAssociatedTestTemplateLimits } from "@/screens/Products/components/Product/Quality/TestTemplates/utils"
import { formatUserName } from "@/utils/user"

interface UseQualityApprovalColumnsProps {
  onTestReview: (qualityApproval: QualityApproval) => void
}

export const useQualityApprovalColumns = ({
  onTestReview,
}: UseQualityApprovalColumnsProps): ColumnDef<QualityApproval>[] => {
  const baseColumns = useProductTestResultColumns({ isApprovalRequest: false })

  return [
    ...baseColumns,
    {
      accessorKey: "comparison",
      header: "Comparison",
      cell: ({ row }) =>
        getTestTemplateComparisonLabel(
          row.original.productTestResult?.associatedTestTemplate?.testTemplate?.comparison ?? ""
        ),
      enableSorting: false,
    },
    {
      accessorKey: "limits",
      header: "Limit(s)",
      cell: ({ row }) =>
        formatAssociatedTestTemplateLimits(
          row.original.productTestResult?.associatedTestTemplate ?? ({} as AssociatedTestTemplate)
        ),
      enableSorting: false,
    },
    {
      accessorKey: "resultsUnitOfMeasure",
      header: "Results UoM",
      cell: ({ row }) => (
        <Chip
          label={row.original.productTestResult?.value || "-"}
          size='small'
          className='rounded bg-primary-100 text-xs text-gray-900'
        />
      ),
      enableSorting: false,
    },
    {
      accessorKey: "submittedBy",
      header: "Submitted By",
      cell: ({ row }) => formatUserName(row.original.createdByUser),
      enableSorting: false,
    },
    {
      accessorKey: "assignedTo",
      header: "Assigned To",
      cell: ({ row }) =>
        formatUserName(row.original.productTestResult?.productTest?.product?.classification?.productManager),
      enableSorting: false,
    },
    {
      accessorKey: "actions",
      header: "",
      cell: ({ row }) => (
        <Button
          appearance='text'
          size='small'
          startIcon={<VisibilityOutlined />}
          onClick={() => onTestReview(row.original)}
          data-testid='dashboard.quality-approval.review-test-button'
        >
          Review Test
        </Button>
      ),
      size: 50,
      enableSorting: false,
    },
  ]
}
